// Travel Mode catalogue — a VIEW over the canonical game registry (gameCatalog GAMES) + existing hubs.
// No new activities, no duplicate records. Availability comes ONLY from the generated disconnected-network test.
import { GAMES } from "@/lib/gameCatalog";
import { SERIES } from "@/data/stories";
import offline from "@/data/travelOffline.json";

const GAME = Object.fromEntries(GAMES.map((g) => [g.id, g]));
const verified = (id) => offline?.results?.[id]?.status || "unverified"; // offline_ready | online_only | unverified

const EXTRA = {
  globe:      { id: "globe",      to: "/globe",      label: "3D Globe",       art: "/generated/game-globe.png",      ageMin: 4, ageMax: 15 },
  continents: { id: "continents", to: "/continents", label: "Continents",     art: "/generated/game-continents.png", ageMin: 4, ageMax: 15 },
  coloring:   { id: "coloring",   to: "/coloring",   label: "Colour & Learn", art: "/generated/game-coloring.png",   ageMin: 4, ageMax: 15 },
};
// Duel games share art with their solo cousins in the registry; Travel shows each its own matching tile.
const TRAVEL_ART = { "duel-quiz": "game-duel-quiz", "duel-tables": "game-duel-tables", "duel-spell": "game-duel-spell", "duel-continent": "game-duel-continent", "duel-reaction": "game-duel-reaction" };
// 256px WebP thumbs of the same artwork (≈3–5 KB each) — tiles render at 56–72px, so the 1024px PNGs are never needed here.
export const artThumb = (id, art) => {
  const name = TRAVEL_ART[id] || (art || "").match(/\/generated\/((?:game|icon)-[a-z0-9-]+)\.png$/)?.[1];
  return name ? `/generated/thumbs/${name}.webp` : art;
};

// Travel-only one-liners (presentation copy, no new activity records). Plain, fun, no syllabus words.
const LINE = {
  squishy: "Hop over the gaps!", dodge: "Weave past the obstacles.", "duel-reaction": "Wait for green… then tap!", "match-pairs": "Flip the cards, find the pairs.",
  "tic-tac-toe": "Three in a row wins.", "first-quest": "One gentle quest per continent.", "speed-tables": "Beat the clock with numbers.", "math-snap": "Snap when the numbers match!",
  "spell-it": "Pick the letters, build the word.", "key-quest": "Type your way through the quest.", "sign-sprint": "Race through hand signs.", "duel-tables": "Two players, one times-table race.",
  "duel-spell": "Spell-off with a friend.", "duel-quiz": "Two players race to answer.", globe: "Spin the world and visit anywhere.", continents: "Hop from continent to continent.",
  "label-it": "Tap the right part of the picture.", "treasure-quest": "Follow the clues to the treasure.", "duel-continent": "Flag to continent — fastest tap wins.", "quick-quiz": "Five quick questions, one tap.",
  "code-breaker": "Crack the secret number code.", "sequence-it": "Put the cards in order.", puzzles: "Drag the pieces into place.", "word-builder": "Spell the word from the clue.",
  "word-wheel": "Spin the wheel, name a thing!", "spelling-bee": "Hear a word, spell it back.", "clock-game": "What time is it? Read the hands.", "sign-match": "Match a hand sign to its meaning.",
  coloring: "Colour pictures from around the world.", "pixel-art": "Paint pixel by pixel.", kaleidoscope: "Paint patterns that mirror themselves.", "stained-glass": "Design a window of light.", mandala: "Draw calm patterns from the centre.",
};

const pick = (ids) => ids.map((id) => GAME[id] || EXTRA[id]).filter(Boolean).map((g) => ({
  id: g.id, to: g.to, label: g.label, art: artThumb(g.id, g.art), line: LINE[g.id] || g.tagline || "", ageMin: g.ageMin ?? 4, ageMax: g.ageMax ?? 15, availability: verified(g.id),
}));

export const DESTINATIONS = [
  { key: "play", art: "/generated/travel-play.webp",    title: "Let's Play",        emoji: "🎮", tint: "mwq-card-tint-primary",
    ids: ["squishy", "dodge", "duel-reaction", "match-pairs", "tic-tac-toe", "first-quest", "speed-tables", "math-snap", "spell-it", "key-quest", "sign-sprint", "duel-tables", "duel-spell", "duel-quiz"] },
  { key: "explore", art: "/generated/travel-explore.webp", title: "Explore the World", emoji: "🌍", tint: "mwq-card-tint-success",
    ids: ["globe", "continents", "label-it", "treasure-quest", "duel-continent", "quick-quiz", "first-quest"] },
  { key: "brain", art: "/generated/travel-brain.webp",   title: "Brain Games",       emoji: "🧩", tint: "mwq-card-tint-reward",
    ids: ["code-breaker", "sequence-it", "puzzles", "word-builder", "word-wheel", "spelling-bee", "clock-game", "sign-match", "quick-quiz"] },
  { key: "create", art: "/generated/travel-create.webp",  title: "Create",            emoji: "🎨", tint: "mwq-card-tint-creative",
    ids: ["coloring", "pixel-art", "kaleidoscope", "stained-glass", "mandala"] },
  { key: "stories", art: "/generated/travel-stories.webp", title: "Stories",           emoji: "📖", tint: "mwq-card-tint-primary", ids: [] },
  { key: "mine", art: "/generated/travel-mine.webp",    title: "My Adventures",     emoji: "🎒", tint: "mwq-card-tint-reward", ids: [] },
];

const STORY_ITEMS = (SERIES || []).map((s) => ({
  id: `story:${s.slug}`, to: `/story/${s.slug}`, label: s.title, art: s.cover || "/generated/tile-adventure.webp", line: s.blurb || "",
  ageMin: s.band === "seedlings" ? 4 : s.band === "pioneers" ? 11 : 7, ageMax: s.band === "seedlings" ? 7 : s.band === "pioneers" ? 15 : 11,
  availability: verified("stories"), // the Stories hub + readers are static content; verified via the /stories route check
}));

export function itemsFor(key) {
  const d = DESTINATIONS.find((x) => x.key === key);
  if (!d) return [];
  if (key === "stories") return STORY_ITEMS;
  return pick(d.ids);
}

export function allItems() {
  const seen = new Set(); const out = [];
  for (const d of DESTINATIONS) for (const it of itemsFor(d.key)) if (!seen.has(it.id)) { seen.add(it.id); out.push(it); }
  return out;
}
export const byId = (id) => allItems().find((x) => x.id === id);
/** Soft category colour for an activity (its first home destination). */
export const tintFor = (id) => (id.startsWith("story:") ? "mwq-card-tint-primary" : DESTINATIONS.find((d) => d.ids.includes(id))?.tint || "mwq-card-tint-primary");

/** Age band ordering only (presentation/recommendation) — nothing is locked. */
export function orderForBand(items, band) {
  const [lo, hi] = band === "4-6" ? [4, 6] : band === "11-15" ? [11, 15] : [7, 10];
  const fit = (it) => (it.ageMin <= hi && it.ageMax >= lo ? 0 : 1);
  return [...items].sort((a, b) => fit(a) - fit(b));
}

export const offlineReady = (items) => items.filter((it) => it.availability === "offline_ready");

/** Surprise Me: offline → verified offline-ready only; online → anything not online-only. Age band is a soft filter. */
export function pickSurprise(online, band, exceptId) {
  const all = allItems();
  const pool = (online ? all.filter((x) => x.availability !== "online_only") : offlineReady(all)).filter((x) => x.id !== exceptId);
  const [lo, hi] = band === "4-6" ? [4, 6] : band === "11-15" ? [11, 15] : [7, 10];
  const fit = pool.filter((x) => x.ageMin <= hi && x.ageMax >= lo);
  const src = fit.length ? fit : pool;
  return src[Math.floor(Math.random() * src.length)] || null;
}
export const OFFLINE_CHECKED_AT = offline?.checked_at || null;
